/*
 * Graph layouts for Diagram. Pure functions over { nodes, edges } where every
 * node has an id, width and height; each returns positions keyed by node id
 * ({ x, y } is the node centre) plus the layer index used for routing.
 *
 *   treeLayout(graph, opts)     tidy tree for hierarchy and org charts
 *   layeredLayout(graph, opts)  layered DAG (Sugiyama-style) for dependency maps
 *   mindMapLayout(graph, opts)  root in the middle, branches left and right
 *
 * opts: { orientation: 'TopToBottom' | 'BottomToTop' | 'LeftToRight' | 'RightToLeft',
 *         horizontalSpacing, verticalSpacing, getBranch(node) }
 */

const VERTICAL = (o) => o === 'TopToBottom' || o === 'BottomToTop' || o === 'Vertical';

/** Adjacency helpers shared by the layouts. */
function buildIndex(graph) {
	const byId = new Map();
	graph.nodes.forEach((n) => byId.set(n.id, n));
	const out = new Map(), inn = new Map();
	graph.nodes.forEach((n) => { out.set(n.id, []); inn.set(n.id, []); });
	graph.edges.forEach((e) => {
		if (!byId.has(e.source) || !byId.has(e.target) || e.source === e.target) return;
		out.get(e.source).push(e.target);
		inn.get(e.target).push(e.source);
	});
	return { byId, out, inn };
}

/** Roots are nodes without incoming edges; a fully cyclic graph falls back to its first node. */
function findRoots(graph, idx) {
	const roots = graph.nodes.filter((n) => idx.inn.get(n.id).length === 0).map((n) => n.id);
	return roots.length ? roots : (graph.nodes.length ? [graph.nodes[0].id] : []);
}

/** Spanning forest by breadth-first walk from the roots; the first parent to reach a node keeps it. */
function spanningForest(graph, idx, roots) {
	const children = new Map(), parent = new Map();
	graph.nodes.forEach((n) => children.set(n.id, []));
	const queue = [...roots]; roots.forEach((r) => parent.set(r, null));
	while (queue.length) {
		const id = queue.shift();
		idx.out.get(id).forEach((c) => {
			if (parent.has(c)) return;
			parent.set(c, id); children.get(id).push(c); queue.push(c);
		});
	}
	// Nodes unreachable from any root (inside cycles) become extra roots.
	graph.nodes.forEach((n) => { if (!parent.has(n.id)) { parent.set(n.id, null); roots.push(n.id); } });
	return { children, parent };
}

// ------------------------------------------------------------------ tree
export function treeLayout(graph, opts = {}) {
	const orientation = opts.orientation || 'TopToBottom';
	const vertical = VERTICAL(orientation);
	const hs = opts.horizontalSpacing === undefined ? 30 : opts.horizontalSpacing;
	const vs = opts.verticalSpacing === undefined ? 50 : opts.verticalSpacing;
	const idx = buildIndex(graph);
	const roots = findRoots(graph, idx);
	const { children } = spanningForest(graph, idx, roots);
	const pos = new Map(), depthOf = new Map();
	// Breadth (across) is the axis children spread on; depth is the axis levels stack on.
	const breadthOf = (n) => vertical ? n.width : n.height;
	const depthSize = (n) => vertical ? n.height : n.width;
	const gapAcross = vertical ? hs : vs;
	const gapDown = vertical ? vs : hs;
	const subtree = new Map();
	const measure = (id) => {
		const kids = children.get(id);
		const own = breadthOf(idx.byId.get(id));
		if (!kids.length) { subtree.set(id, own); return own; }
		let total = 0; kids.forEach((k, i) => { total += measure(k) + (i ? gapAcross : 0); });
		const w = Math.max(own, total); subtree.set(id, w); return w;
	};
	const levelDepth = [];
	const assignDepth = (id, d) => { depthOf.set(id, d); levelDepth[d] = Math.max(levelDepth[d] || 0, depthSize(idx.byId.get(id))); children.get(id).forEach((k) => assignDepth(k, d + 1)); };
	roots.forEach((r) => { measure(r); assignDepth(r, 0); });
	const levelStart = []; let acc = 0;
	levelDepth.forEach((size, i) => { levelStart[i] = acc; acc += size + gapDown; });
	const place = (id, start) => {
		const kids = children.get(id);
		const w = subtree.get(id);
		const centre = start + w / 2;
		const d = depthOf.get(id);
		const along = levelStart[d] + levelDepth[d] / 2;
		pos.set(id, vertical ? { x: centre, y: along } : { x: along, y: centre });
		let cursor = start + (w - (kids.reduce((s, k, i) => s + subtree.get(k) + (i ? gapAcross : 0), 0))) / 2;
		kids.forEach((k) => { place(k, cursor); cursor += subtree.get(k) + gapAcross; });
	};
	let cursor = 0;
	roots.forEach((r) => { place(r, cursor); cursor += subtree.get(r) + gapAcross * 2; });
	flip(pos, orientation);
	return { positions: pos, layers: depthOf };
}

// ------------------------------------------------------------------ layered DAG
export function layeredLayout(graph, opts = {}) {
	const orientation = opts.orientation || 'TopToBottom';
	const vertical = VERTICAL(orientation);
	const hs = opts.horizontalSpacing === undefined ? 40 : opts.horizontalSpacing;
	const vs = opts.verticalSpacing === undefined ? 60 : opts.verticalSpacing;
	const idx = buildIndex(graph);
	const ids = graph.nodes.map((n) => n.id);
	// 1. Break cycles: edges that close a cycle in a depth-first walk are reversed for layering only.
	const state = new Map(); const edges = [];
	const seenEdge = new Set();
	const visit = (id) => {
		state.set(id, 1);
		idx.out.get(id).forEach((t) => {
			const key = id + '' + t; if (seenEdge.has(key)) return; seenEdge.add(key);
			if (state.get(t) === 1) edges.push([t, id]); else { edges.push([id, t]); if (!state.has(t)) visit(t); }
		});
		state.set(id, 2);
	};
	ids.forEach((id) => { if (!state.has(id)) visit(id); });
	const succ = new Map(), pred = new Map();
	ids.forEach((id) => { succ.set(id, []); pred.set(id, []); });
	edges.forEach(([s, t]) => { succ.get(s).push(t); pred.get(t).push(s); });
	// 2. Longest-path layering.
	const layer = new Map();
	const depth = (id) => { if (layer.has(id)) return layer.get(id); layer.set(id, 0); const d = pred.get(id).length ? Math.max(...pred.get(id).map(depth)) + 1 : 0; layer.set(id, d); return d; };
	ids.forEach(depth);
	const layers = [];
	ids.forEach((id) => { const l = layer.get(id); (layers[l] = layers[l] || []).push(id); });
	// 3. Ordering: barycenter sweeps down and up.
	const order = new Map(); layers.forEach((l) => l.forEach((id, i) => order.set(id, i)));
	const bary = (id, neigh) => { const ns = neigh.get(id); if (!ns.length) return order.get(id); return ns.reduce((s, n) => s + order.get(n), 0) / ns.length; };
	for (let sweep = 0; sweep < 6; sweep++) {
		const down = sweep % 2 === 0;
		const seq = down ? layers.map((_, i) => i) : layers.map((_, i) => layers.length - 1 - i);
		seq.forEach((li) => {
			const neigh = down ? pred : succ;
			const arr = layers[li].map((id) => ({ id, b: bary(id, neigh) }));
			arr.sort((a, b) => a.b - b.b || order.get(a.id) - order.get(b.id));
			layers[li] = arr.map((a) => a.id); layers[li].forEach((id, i) => order.set(id, i));
		});
	}
	// 4. Coordinates: pack each layer, then pull nodes towards the mean of their neighbours and re-separate.
	const across = (n) => vertical ? n.width : n.height;
	const along = (n) => vertical ? n.height : n.width;
	const gapAcross = vertical ? hs : vs, gapAlong = vertical ? vs : hs;
	const coord = new Map();
	layers.forEach((l) => { let c = 0; l.forEach((id) => { const w = across(idx.byId.get(id)); coord.set(id, c + w / 2); c += w + gapAcross; }); });
	const separate = (l) => {
		for (let i = 1; i < l.length; i++) {
			const a = l[i - 1], b = l[i];
			const min = coord.get(a) + across(idx.byId.get(a)) / 2 + gapAcross + across(idx.byId.get(b)) / 2;
			if (coord.get(b) < min) coord.set(b, min);
		}
		for (let i = l.length - 2; i >= 0; i--) {
			const a = l[i], b = l[i + 1];
			const max = coord.get(b) - across(idx.byId.get(b)) / 2 - gapAcross - across(idx.byId.get(a)) / 2;
			if (coord.get(a) > max) coord.set(a, max);
		}
	};
	for (let pass = 0; pass < 4; pass++) {
		const down = pass % 2 === 0;
		const seq = down ? layers.map((_, i) => i) : layers.map((_, i) => layers.length - 1 - i);
		seq.forEach((li) => {
			const neigh = down ? pred : succ;
			layers[li].forEach((id) => { const ns = neigh.get(id); if (ns.length) coord.set(id, ns.reduce((s, n) => s + coord.get(n), 0) / ns.length); });
			layers[li].sort((a, b) => coord.get(a) - coord.get(b));
			separate(layers[li]);
		});
	}
	// Centre every layer on the widest one.
	const extent = layers.map((l) => { const f = l[0], e = l[l.length - 1]; return [coord.get(f) - across(idx.byId.get(f)) / 2, coord.get(e) + across(idx.byId.get(e)) / 2]; });
	const min = Math.min(...extent.map((e) => e[0])), max = Math.max(...extent.map((e) => e[1]));
	const mid = (min + max) / 2;
	const layerAlong = layers.map((l) => Math.max(...l.map((id) => along(idx.byId.get(id)))));
	const layerStart = []; let acc = 0; layerAlong.forEach((s, i) => { layerStart[i] = acc; acc += s + gapAlong; });
	const pos = new Map();
	layers.forEach((l, li) => {
		const shift = mid - (extent[li][0] + extent[li][1]) / 2;
		const a = layerStart[li] + layerAlong[li] / 2;
		l.forEach((id) => { const c = coord.get(id) + shift - min; pos.set(id, vertical ? { x: c, y: a } : { x: a, y: c }); });
	});
	flip(pos, orientation);
	return { positions: pos, layers: layer };
}

// ------------------------------------------------------------------ mind map
export function mindMapLayout(graph, opts = {}) {
	const hs = opts.horizontalSpacing === undefined ? 50 : opts.horizontalSpacing;
	const vs = opts.verticalSpacing === undefined ? 30 : opts.verticalSpacing;
	const idx = buildIndex(graph);
	const roots = findRoots(graph, idx);
	const rootId = opts.root && idx.byId.has(opts.root) ? opts.root : roots[0];
	if (!rootId) return { positions: new Map(), layers: new Map() };
	const { children } = spanningForest(graph, idx, [rootId]);
	const branchOf = new Map();
	const firstLevel = children.get(rootId);
	firstLevel.forEach((id, i) => {
		let b = opts.getBranch ? opts.getBranch(idx.byId.get(id)) : null;
		b = /left/i.test(String(b)) ? 'Left' : /right/i.test(String(b)) ? 'Right' : (i % 2 === 0 ? 'Right' : 'Left');
		branchOf.set(id, b);
	});
	const sideOf = (id) => { let n = id; while (!branchOf.has(n)) { const p = [...children.entries()].find(([, kids]) => kids.includes(n)); if (!p) return 'Right'; n = p[0]; } return branchOf.get(n); };
	const side = { Left: [], Right: [] };
	firstLevel.forEach((id) => side[branchOf.get(id)].push(id));
	const pos = new Map(), layers = new Map();
	const rootNode = idx.byId.get(rootId);
	pos.set(rootId, { x: 0, y: 0 }); layers.set(rootId, 0);
	const height = new Map();
	const measure = (id) => { const kids = children.get(id); const own = idx.byId.get(id).height; if (!kids.length) { height.set(id, own); return own; } let t = 0; kids.forEach((k, i) => { t += measure(k) + (i ? vs : 0); }); const h = Math.max(own, t); height.set(id, h); return h; };
	const place = (id, top, x, dir, depth) => {
		const kids = children.get(id); const h = height.get(id); const node = idx.byId.get(id);
		pos.set(id, { x: x + dir * node.width / 2, y: top + h / 2 }); layers.set(id, depth);
		const kidsTotal = kids.reduce((s, k, i) => s + height.get(k) + (i ? vs : 0), 0);
		let cursor = top + (h - kidsTotal) / 2;
		const nextX = x + dir * (node.width + hs);
		kids.forEach((k) => { place(k, cursor, nextX, dir, depth + 1); cursor += height.get(k) + vs; });
	};
	['Right', 'Left'].forEach((s) => {
		const dir = s === 'Right' ? 1 : -1;
		const ids = side[s]; ids.forEach(measure);
		const total = ids.reduce((sum, k, i) => sum + height.get(k) + (i ? vs : 0), 0);
		let cursor = -total / 2;
		const x = dir * (rootNode.width / 2 + hs);
		ids.forEach((k) => { place(k, cursor, x, dir, 1); cursor += height.get(k) + vs; });
	});
	return { positions: pos, layers, branchOf: new Map(graph.nodes.map((n) => [n.id, n.id === rootId ? 'Root' : sideOf(n.id)])) };
}

/** Mirrors positions for the reversed orientations. */
function flip(pos, orientation) {
	if (orientation === 'BottomToTop') pos.forEach((p) => { p.y = -p.y; });
	if (orientation === 'RightToLeft') pos.forEach((p) => { p.x = -p.x; });
}
